import React, { useEffect, useState, useCallback } from 'react'
import {
  View,
  Text,
  StyleSheet,
  FlatList,
  TouchableOpacity,
  Image,
  RefreshControl,
  Alert,
  useWindowDimensions,
  TextInput,
  Modal,
  ScrollView,
  Platform } from 'react-native'
import { ArrowLeft, Package, Check, Plus, FolderPlus, Folder, X, MoreVertical, Edit2, Trash2, ChevronRight } from 'lucide-react-native'
import { useAuth } from '../contexts/AuthContext'
import { useLanguage } from '@fliisi/i18n'
import { supabase } from '../lib/supabase'
import { NativeStackNavigationProp } from '@react-navigation/native-stack'
import { RootStackParamList } from '../types/navigation'
import theme from '../theme'
import {
  Button,
  ButtonText,
  Card,
  EmptyState,
  Input,
  Spinner,
  TopBar,
  XStack,
  YStack } from '@fliisi/pollen'

const GRID_PADDING = 16
const CARD_GAP = 12

const COLLECTION_COLORS = [
  '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7',
  '#DDA0DD', '#98D8C8', '#F7DC6F', '#BB8FCE', '#85C1E9',
]

interface Collection {
  id: string
  name: string
  name_fi: string | null
  name_en: string | null
  color: string | null
  sort_order: number
  product_count?: number
}

interface Product {
  id: string
  title: string
  title_en: string | null
  title_fi: string | null
  price: number
  status: string
  created_at: string
  collection_id: string | null
  product_images: { image_url: string }[]
}

interface Props {
  navigation: NativeStackNavigationProp<RootStackParamList, 'DraftProducts'>
}

export default function DraftProductsScreen({ navigation }: Props) {
  const { user } = useAuth()
  const { language, t } = useLanguage()
  const { width } = useWindowDimensions()

  const [products, setProducts] = useState<Product[]>([])
  const [collections, setCollections] = useState<Collection[]>([])
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [selectionMode, setSelectionMode] = useState(false)
  const [activeCollection, setActiveCollection] = useState<string | null>(null) // null = all, 'uncategorized' = no collection

  // Collection modal state
  const [showCollectionModal, setShowCollectionModal] = useState(false)
  const [editingCollection, setEditingCollection] = useState<Collection | null>(null)
  const [collectionName, setCollectionName] = useState('')
  const [collectionColor, setCollectionColor] = useState(COLLECTION_COLORS[0])

  // Move to collection modal
  const [showMoveModal, setShowMoveModal] = useState(false)

  const isDesktop = width > 768
  const columns = isDesktop ? Math.min(Math.floor((width - GRID_PADDING * 2) / 180), 5) : 2
  const cardWidth = (width - GRID_PADDING * 2 - CARD_GAP * (columns - 1)) / columns

  const getTitle = (product: Product) => {
    if (language === 'fi' && product.title_fi) return product.title_fi
    if (language === 'en' && product.title_en) return product.title_en
    return product.title || t('untitled')
  }

  const getCollectionName = (collection: Collection) => {
    if (language === 'fi' && collection.name_fi) return collection.name_fi
    if (language === 'en' && collection.name_en) return collection.name_en
    return collection.name
  }

  const fetchData = async () => {
    if (!user?.id) return

    try {
      // Fetch collections
      const { data: collectionsData, error: collectionsError } = await supabase
        .from('product_collections')
        .select('*')
        .eq('user_id', user.id)
        .order('sort_order', { ascending: true })

      if (!collectionsError && collectionsData) {
        setCollections(collectionsData)
      }

      // Fetch products
      const { data: productsData, error: productsError } = await supabase
        .from('products')
        .select(`
          id,
          title,
          title_en,
          title_fi,
          price,
          status,
          created_at,
          collection_id,
          product_images (image_url)
        `)
        .eq('seller_id', user.id)
        .is('reservation_id', null)
        .is('flea_market_id', null)
        .order('created_at', { ascending: false })

      if (productsError) throw productsError
      setProducts(productsData || [])
    } catch (error) {
      console.error('Error fetching draft products:', error)
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }

  useEffect(() => {
    if (!user?.id) {
      navigation.replace('Login')
      return
    }
    fetchData()
  }, [user?.id])

  const onRefresh = () => {
    setRefreshing(true)
    fetchData()
  }

  const toggleSelection = (id: string) => {
    const newSelected = new Set(selectedIds)
    if (newSelected.has(id)) {
      newSelected.delete(id)
    } else {
      newSelected.add(id)
    }
    setSelectedIds(newSelected)
    if (newSelected.size === 0) {
      setSelectionMode(false)
    }
  }

  const selectAll = () => {
    const filteredProducts = getFilteredProducts()
    if (selectedIds.size === filteredProducts.length) {
      setSelectedIds(new Set())
      setSelectionMode(false)
    } else {
      setSelectedIds(new Set(filteredProducts.map(p => p.id)))
    }
  }

  const getFilteredProducts = useCallback(() => {
    if (activeCollection === null) {
      return products
    } else if (activeCollection === 'uncategorized') {
      return products.filter(p => !p.collection_id)
    } else {
      return products.filter(p => p.collection_id === activeCollection)
    }
  }, [products, activeCollection])

  const getCollectionProductCount = (collectionId: string | null) => {
    if (collectionId === null) {
      return products.length
    } else if (collectionId === 'uncategorized') {
      return products.filter(p => !p.collection_id).length
    }
    return products.filter(p => p.collection_id === collectionId).length
  }

  const showAlert = (title: string, message: string, buttons?: any[]) => {
    if (Platform.OS === 'web') {
      if (buttons && buttons.length > 1) {
        if (window.confirm(message)) {
          const destructiveBtn = buttons.find(b => b.style === 'destructive')
          if (destructiveBtn?.onPress) destructiveBtn.onPress()
        }
      } else {
        window.alert(message)
      }
    } else {
      Alert.alert(title, message, buttons)
    }
  }

  const handleDeleteSelected = async () => {
    showAlert(
      t('delete_products') || 'Delete Products',
      `${t('delete_products_confirm') || 'Are you sure you want to delete'} ${selectedIds.size} ${t('single_item') || 'item'}(s)?`,
      [
        { text: t('cancel') || 'Cancel', style: 'cancel' },
        {
          text: t('delete') || 'Delete',
          style: 'destructive',
          onPress: async () => {
            try {
              const { error } = await supabase
                .from('products')
                .delete()
                .in('id', Array.from(selectedIds))

              if (error) throw error

              setSelectedIds(new Set())
              setSelectionMode(false)
              fetchData()
            } catch (error) {
              console.error('Error deleting products:', error)
            }
          } },
      ]
    )
  }

  const handleLinkToReservation = () => {
    navigation.navigate('JoinMarket')
  }

  // Collection management
  const openCreateCollection = () => {
    setEditingCollection(null)
    setCollectionName('')
    setCollectionColor(COLLECTION_COLORS[Math.floor(Math.random() * COLLECTION_COLORS.length)])
    setShowCollectionModal(true)
  }

  const openEditCollection = (collection: Collection) => {
    setEditingCollection(collection)
    setCollectionName(collection.name)
    setCollectionColor(collection.color || COLLECTION_COLORS[0])
    setShowCollectionModal(true)
  }

  const saveCollection = async () => {
    if (!user?.id || !collectionName.trim()) return

    try {
      if (editingCollection) {
        // Update existing
        const { error } = await supabase
          .from('product_collections')
          .update({
            name: collectionName.trim(),
            color: collectionColor })
          .eq('id', editingCollection.id)

        if (error) throw error
      } else {
        // Create new
        const { error } = await supabase
          .from('product_collections')
          .insert({
            user_id: user.id,
            name: collectionName.trim(),
            color: collectionColor,
            sort_order: collections.length })

        if (error) throw error
      }

      setShowCollectionModal(false)
      fetchData()
    } catch (error) {
      console.error('Error saving collection:', error)
    }
  }

  const deleteCollection = async (collection: Collection) => {
    showAlert(
      t('delete_collection') || 'Delete Collection',
      t('confirm_delete_collection') || 'Delete this collection? Items will be moved to Uncategorized.',
      [
        { text: t('cancel') || 'Cancel', style: 'cancel' },
        {
          text: t('delete') || 'Delete',
          style: 'destructive',
          onPress: async () => {
            try {
              // First, remove collection_id from products
              await supabase
                .from('products')
                .update({ collection_id: null })
                .eq('collection_id', collection.id)

              // Then delete collection
              const { error } = await supabase
                .from('product_collections')
                .delete()
                .eq('id', collection.id)

              if (error) throw error

              if (activeCollection === collection.id) {
                setActiveCollection(null)
              }
              fetchData()
            } catch (error) {
              console.error('Error deleting collection:', error)
            }
          } },
      ]
    )
  }

  const moveToCollection = async (targetCollectionId: string | null) => {
    try {
      const { error } = await supabase
        .from('products')
        .update({ collection_id: targetCollectionId })
        .in('id', Array.from(selectedIds))

      if (error) throw error

      setShowMoveModal(false)
      setSelectedIds(new Set())
      setSelectionMode(false)
      fetchData()
    } catch (error) {
      console.error('Error moving products:', error)
    }
  }

  const filteredProducts = getFilteredProducts()

  const renderCollectionTab = (collection: Collection | { id: string | null, name: string }) => {
    const isActive = activeCollection === collection.id
    const count = getCollectionProductCount(collection.id)
    const color = 'color' in collection ? collection.color : null

    return (
      <TouchableOpacity
        key={collection.id || 'all'}
        style={[
          styles.collectionTab,
          isActive && styles.collectionTabActive,
          color && isActive && { borderBottomColor: color },
        ]}
        onPress={() => setActiveCollection(collection.id)}
        onLongPress={() => {
          if ('color' in collection && collection.id) {
            openEditCollection(collection as Collection)
          }
        }}
      >
        {color && (
          <View style={[styles.colorDot, { backgroundColor: color }]} />
        )}
        <Text
          style={[
            styles.collectionTabText,
            isActive && styles.collectionTabTextActive,
          ]}
          numberOfLines={1}
        >
          {'name_fi' in collection ? getCollectionName(collection as Collection) : collection.name}
        </Text>
        <Text style={styles.collectionCount}>{count}</Text>
      </TouchableOpacity>
    )
  }

  const renderProduct = ({ item }: { item: Product }) => {
    const isSelected = selectedIds.has(item.id)
    const collection = collections.find(c => c.id === item.collection_id)

    return (
      <TouchableOpacity
        style={[
          styles.productCard,
          { width: cardWidth },
          isSelected && styles.productCardSelected,
        ]}
        onPress={() => {
          if (selectionMode) {
            toggleSelection(item.id)
          } else {
            navigation.navigate('ProductDetail', { productId: item.id })
          }
        }}
        onLongPress={() => {
          setSelectionMode(true)
          toggleSelection(item.id)
        }}
      >
        {selectionMode && (
          <View style={[styles.checkbox, isSelected && styles.checkboxSelected]}>
            {isSelected && <Check size={14} color={theme.colors.text.inverse} strokeWidth={3} />}
          </View>
        )}
        {collection && (
          <View style={[styles.collectionBadge, { backgroundColor: collection.color || theme.colors.ui.border }]}>
            <Text style={styles.collectionBadgeText} numberOfLines={1}>
              {getCollectionName(collection)}
            </Text>
          </View>
        )}
        <Image
          source={{ uri: item.product_images?.[0]?.image_url || undefined }}
          style={styles.productImage}
        />
        <View style={styles.productInfo}>
          <Text style={styles.productTitle} numberOfLines={2}>{getTitle(item)}</Text>
          <Text style={styles.productPrice}>€{item.price?.toFixed(2) || '0.00'}</Text>
          <View style={styles.draftBadge}>
            <Text style={styles.draftBadgeText}>{t('draft') || 'Draft'}</Text>
          </View>
        </View>
      </TouchableOpacity>
    )
  }

  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backButton}>
          <ArrowLeft size={18} color={theme.colors.brand.primary} />
          <Text style={styles.backButtonText}>{t('back') || 'Back'}</Text>
        </TouchableOpacity>
        <Text style={styles.headerTitle}>{t('drafts') || 'Drafts'}</Text>
        <View style={styles.headerActions}>
          <TouchableOpacity onPress={openCreateCollection} style={styles.headerIconButton}>
            <FolderPlus size={20} color={theme.colors.brand.primary} />
          </TouchableOpacity>
          {products.length > 0 && (
            <TouchableOpacity onPress={() => setSelectionMode(!selectionMode)}>
              <Text style={styles.selectButton}>
                {selectionMode ? (t('done') || 'Done') : (t('select') || 'Select')}
              </Text>
            </TouchableOpacity>
          )}
        </View>
      </View>

      {/* Collection Tabs */}
      <View style={styles.collectionsContainer}>
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={styles.collectionTabs}
        >
          {renderCollectionTab({ id: null, name: t('all_items') || 'All Items' })}
          {renderCollectionTab({ id: 'uncategorized', name: t('uncategorized') || 'Uncategorized' })}
          {collections.map(collection => renderCollectionTab(collection))}
          <TouchableOpacity style={styles.addCollectionTab} onPress={openCreateCollection}>
            <Plus size={16} color={theme.colors.brand.primary} />
          </TouchableOpacity>
        </ScrollView>
      </View>

      {/* Selection Actions */}
      {selectionMode && selectedIds.size > 0 && (
        <View style={styles.selectionBar}>
          <TouchableOpacity style={styles.selectionAction} onPress={selectAll}>
            <Text style={styles.selectionActionText}>
              {selectedIds.size === filteredProducts.length
                ? (t('deselect_all_items') || 'Deselect All')
                : (t('select_all_items') || 'Select All')}
            </Text>
          </TouchableOpacity>
          <View style={styles.selectionActions}>
            <TouchableOpacity
              style={[styles.actionButton, styles.moveButton]}
              onPress={() => setShowMoveModal(true)}
            >
              <Folder size={14} color={theme.colors.brand.primary} />
              <Text style={styles.moveButtonText}>
                {t('move_to_collection') || 'Move'}
              </Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.actionButton, styles.linkButton]}
              onPress={handleLinkToReservation}
            >
              <Text style={styles.linkButtonText}>
                {t('link_to_booth') || 'Link'} ({selectedIds.size})
              </Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.actionButton, styles.deleteButton]}
              onPress={handleDeleteSelected}
            >
              <Text style={styles.deleteButtonText}>{t('delete') || 'Delete'}</Text>
            </TouchableOpacity>
          </View>
        </View>
      )}

      {/* Info Banner */}
      {!selectionMode && products.length > 0 && activeCollection === null && (
        <View style={styles.infoBanner}>
          <Text style={styles.infoText}>
            {t('draft_products_info') || 'These products are not yet linked to any booth. Long press to select multiple.'}
          </Text>
        </View>
      )}

      {/* Product List */}
      <FlatList
        data={filteredProducts}
        keyExtractor={(item) => item.id}
        numColumns={columns}
        key={columns}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} />}
        contentContainerStyle={styles.listContent}
        columnWrapperStyle={columns > 1 ? styles.row : undefined}
        ListEmptyComponent={
          <View style={styles.emptyState}>
            <View style={styles.emptyIconContainer}>
              <Package size={64} color={theme.colors.text.secondary} />
            </View>
            <Text style={styles.emptyText}>
              {activeCollection && activeCollection !== 'uncategorized'
                ? (t('no_products') || 'No products')
                : (t('no_draft_products') || 'No draft products')}
            </Text>
            <Text style={styles.emptySubtext}>
              {t('create_products_hint') || 'Take photos of items you want to sell'}
            </Text>
            <TouchableOpacity
              style={styles.createButton}
              onPress={() => navigation.navigate('Camera')}
            >
              <Text style={styles.createButtonText}>{t('add_product') || 'Add Product'}</Text>
            </TouchableOpacity>
          </View>
        }
        renderItem={renderProduct}
      />

      {/* Create/Edit Collection Modal */}
      <Modal
        visible={showCollectionModal}
        transparent
        animationType="fade"
        onRequestClose={() => setShowCollectionModal(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>
                {editingCollection ? (t('edit_collection') || 'Edit Collection') : (t('create_collection') || 'Create Collection')}
              </Text>
              <TouchableOpacity onPress={() => setShowCollectionModal(false)}>
                <X size={24} color={theme.colors.text.secondary} />
              </TouchableOpacity>
            </View>

            <Text style={styles.inputLabel}>{t('collection_name') || 'Collection Name'}</Text>
            <TextInput
              style={styles.input}
              value={collectionName}
              onChangeText={setCollectionName}
              placeholder={t('collection_name_placeholder') || 'e.g., Summer clothes...'}
              placeholderTextColor={theme.colors.text.secondary}
            />

            <Text style={styles.inputLabel}>{t('collection_color') || 'Color'}</Text>
            <View style={styles.colorPicker}>
              {COLLECTION_COLORS.map(color => (
                <TouchableOpacity
                  key={color}
                  style={[
                    styles.colorOption,
                    { backgroundColor: color },
                    collectionColor === color && styles.colorOptionSelected,
                  ]}
                  onPress={() => setCollectionColor(color)}
                >
                  {collectionColor === color && (
                    <Check size={16} color="#fff" strokeWidth={3} />
                  )}
                </TouchableOpacity>
              ))}
            </View>

            <View style={styles.modalActions}>
              {editingCollection && (
                <TouchableOpacity
                  style={styles.deleteCollectionButton}
                  onPress={() => {
                    setShowCollectionModal(false)
                    deleteCollection(editingCollection)
                  }}
                >
                  <Trash2 size={18} color="#E53935" />
                </TouchableOpacity>
              )}
              <Button variant="outline" onPress={() => setShowCollectionModal(false)}><ButtonText>{t('cancel') || 'Cancel'}</ButtonText></Button>
              <TouchableOpacity
                style={[styles.saveButton, !collectionName.trim() && styles.saveButtonDisabled]}
                onPress={saveCollection}
                disabled={!collectionName.trim()}
              >
                <Text style={styles.saveButtonText}>{t('save') || 'Save'}</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* Move to Collection Modal */}
      <Modal
        visible={showMoveModal}
        transparent
        animationType="fade"
        onRequestClose={() => setShowMoveModal(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>{t('move_to_collection') || 'Move to Collection'}</Text>
              <TouchableOpacity onPress={() => setShowMoveModal(false)}>
                <X size={24} color={theme.colors.text.secondary} />
              </TouchableOpacity>
            </View>

            <ScrollView style={styles.collectionList}>
              <TouchableOpacity
                style={styles.collectionListItem}
                onPress={() => moveToCollection(null)}
              >
                <View style={[styles.colorDot, { backgroundColor: theme.colors.ui.border }]} />
                <Text style={styles.collectionListItemText}>{t('uncategorized') || 'Uncategorized'}</Text>
                <ChevronRight size={18} color={theme.colors.text.secondary} />
              </TouchableOpacity>
              {collections.map(collection => (
                <TouchableOpacity
                  key={collection.id}
                  style={styles.collectionListItem}
                  onPress={() => moveToCollection(collection.id)}
                >
                  <View style={[styles.colorDot, { backgroundColor: collection.color || theme.colors.ui.border }]} />
                  <Text style={styles.collectionListItemText}>{getCollectionName(collection)}</Text>
                  <ChevronRight size={18} color={theme.colors.text.secondary} />
                </TouchableOpacity>
              ))}
            </ScrollView>

            {collections.length === 0 && (
              <View style={styles.noCollectionsHint}>
                <Text style={styles.noCollectionsText}>{t('no_collections') || 'No collections yet'}</Text>
                <TouchableOpacity
                  style={styles.createCollectionHintButton}
                  onPress={() => {
                    setShowMoveModal(false)
                    openCreateCollection()
                  }}
                >
                  <Plus size={16} color={theme.colors.brand.primary} />
                  <Text style={styles.createCollectionHintText}>{t('create_collection') || 'Create Collection'}</Text>
                </TouchableOpacity>
              </View>
            )}
          </View>
        </View>
      </Modal>
</View>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background.primary },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingTop: 16,
    paddingBottom: 12,
    backgroundColor: theme.colors.background.card,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.ui.border },
  backButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 4 },
  backButtonText: {
    fontSize: 16,
    color: theme.colors.brand.primary,
    fontWeight: '500',
    marginLeft: 4 },
  headerTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: theme.colors.text.primary },
  headerActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12 },
  headerIconButton: {
    padding: 4 },
  selectButton: {
    fontSize: 16,
    color: theme.colors.brand.primary,
    fontWeight: '500' },
  collectionsContainer: {
    backgroundColor: theme.colors.background.card,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.ui.border },
  collectionTabs: {
    flexDirection: 'row',
    paddingHorizontal: 12,
    gap: 4 },
  collectionTab: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderBottomWidth: 2,
    borderBottomColor: 'transparent',
    gap: 6 },
  collectionTabActive: {
    borderBottomColor: theme.colors.brand.primary },
  collectionTabText: {
    fontSize: 14,
    color: theme.colors.text.secondary,
    maxWidth: 100 },
  collectionTabTextActive: {
    color: theme.colors.text.primary,
    fontWeight: '600' },
  collectionCount: {
    fontSize: 12,
    color: theme.colors.text.secondary,
    backgroundColor: theme.colors.background.secondary,
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 10,
    overflow: 'hidden' },
  colorDot: {
    width: 10,
    height: 10,
    borderRadius: 5 },
  addCollectionTab: {
    padding: 10,
    justifyContent: 'center',
    alignItems: 'center' },
  selectionBar: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 10,
    backgroundColor: theme.colors.background.secondary,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.ui.border },
  selectionAction: {
    paddingVertical: 4 },
  selectionActionText: {
    fontSize: 14,
    color: theme.colors.brand.primary,
    fontWeight: '500' },
  selectionActions: {
    flexDirection: 'row',
    gap: 8 },
  actionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
    gap: 4 },
  moveButton: {
    backgroundColor: `${theme.colors.brand.primary}15`,
    borderWidth: 1,
    borderColor: theme.colors.brand.primary },
  moveButtonText: {
    color: theme.colors.brand.primary,
    fontSize: 13,
    fontWeight: '600' },
  linkButton: {
    backgroundColor: theme.colors.brand.primary },
  linkButtonText: {
    color: theme.colors.text.inverse,
    fontSize: 13,
    fontWeight: '600' },
  deleteButton: {
    backgroundColor: '#FFEBEE' },
  deleteButtonText: {
    color: '#E53935',
    fontSize: 13,
    fontWeight: '600' },
  infoBanner: {
    backgroundColor: `${theme.colors.brand.primary}10`,
    paddingHorizontal: 16,
    paddingVertical: 10 },
  infoText: {
    fontSize: 13,
    color: theme.colors.text.secondary,
    textAlign: 'center' },
  listContent: {
    padding: GRID_PADDING,
    paddingBottom: 100 },
  row: {
    gap: CARD_GAP,
    marginBottom: CARD_GAP },
  productCard: {
    backgroundColor: theme.colors.background.card,
    borderRadius: 12,
    overflow: 'hidden',
    position: 'relative',
    ...theme.shadows.soft },
  productCardSelected: {
    borderWidth: 2,
    borderColor: theme.colors.brand.primary },
  checkbox: {
    position: 'absolute',
    top: 8,
    left: 8,
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: 'rgba(255,255,255,0.9)',
    borderWidth: 2,
    borderColor: theme.colors.ui.border,
    zIndex: 10,
    justifyContent: 'center',
    alignItems: 'center' },
  checkboxSelected: {
    backgroundColor: theme.colors.brand.primary,
    borderColor: theme.colors.brand.primary },
  collectionBadge: {
    position: 'absolute',
    top: 8,
    right: 8,
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 10,
    zIndex: 10,
    maxWidth: '60%' },
  collectionBadgeText: {
    fontSize: 10,
    fontWeight: '600',
    color: '#fff' },
  productImage: {
    width: '100%',
    aspectRatio: 1,
    backgroundColor: theme.colors.ui.border },
  productInfo: {
    padding: 10 },
  productTitle: {
    fontSize: 14,
    fontWeight: '500',
    color: theme.colors.text.primary,
    marginBottom: 4,
    minHeight: 36 },
  productPrice: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.colors.brand.primary,
    marginBottom: 6 },
  draftBadge: {
    alignSelf: 'flex-start',
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 10,
    backgroundColor: '#9E9E9E' },
  draftBadgeText: {
    fontSize: 11,
    fontWeight: '600',
    color: theme.colors.text.inverse },
  emptyState: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingTop: 60,
    paddingHorizontal: 32 },
  emptyIconContainer: {
    marginBottom: 16 },
  emptyText: {
    fontSize: 18,
    color: theme.colors.text.secondary,
    marginBottom: 8,
    textAlign: 'center' },
  emptySubtext: {
    fontSize: 14,
    color: theme.colors.text.secondary,
    textAlign: 'center',
    marginBottom: 24 },
  createButton: {
    backgroundColor: theme.colors.brand.primary,
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8 },
  createButtonText: {
    color: theme.colors.text.inverse,
    fontWeight: '600',
    fontSize: 16 },
  // Modal styles
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20 },
  modalContent: {
    backgroundColor: theme.colors.background.card,
    borderRadius: 16,
    padding: 20,
    width: '100%',
    maxWidth: 400,
    maxHeight: '80%' },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20 },
  modalTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: theme.colors.text.primary },
  inputLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: theme.colors.text.primary,
    marginBottom: 8 },
  input: {
    backgroundColor: theme.colors.background.secondary,
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 12,
    fontSize: 16,
    color: theme.colors.text.primary,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: theme.colors.ui.border },
  colorPicker: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
    marginBottom: 24 },
  colorOption: {
    width: 36,
    height: 36,
    borderRadius: 18,
    justifyContent: 'center',
    alignItems: 'center' },
  colorOptionSelected: {
    borderWidth: 3,
    borderColor: '#fff',
    ...theme.shadows.soft },
  modalActions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 12 },
  deleteCollectionButton: {
    padding: 10,
    marginRight: 'auto' },
  cancelButton: {
    paddingHorizontal: 16,
    paddingVertical: 10 },
  cancelButtonText: {
    fontSize: 16,
    color: theme.colors.text.secondary,
    fontWeight: '500' },
  saveButton: {
    backgroundColor: theme.colors.brand.primary,
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: 8 },
  saveButtonDisabled: {
    opacity: 0.5 },
  saveButtonText: {
    fontSize: 16,
    color: theme.colors.text.inverse,
    fontWeight: '600' },
  collectionList: {
    maxHeight: 300 },
  collectionListItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 14,
    paddingHorizontal: 4,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.ui.border,
    gap: 12 },
  collectionListItemText: {
    flex: 1,
    fontSize: 16,
    color: theme.colors.text.primary },
  noCollectionsHint: {
    alignItems: 'center',
    paddingVertical: 20 },
  noCollectionsText: {
    fontSize: 14,
    color: theme.colors.text.secondary,
    marginBottom: 12 },
  createCollectionHintButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 16,
    paddingVertical: 10,
    backgroundColor: `${theme.colors.brand.primary}15`,
    borderRadius: 8 },
  createCollectionHintText: {
    fontSize: 14,
    color: theme.colors.brand.primary,
    fontWeight: '600' } })
